import type shp from 'shpjs';
import { RogoShapefile, RogoShapefileType } from '../types/types';
import { alertWarn } from '../alertDispatcher';
import { GeoData } from '@rogoag/shared-ts';
import { Buffer } from 'buffer';
import { CsvParserService } from './CsvParserService';
import JSZip from 'jszip';

/**
 * Supported geospatial file formats that can be converted to shapefiles
 */
type ConvertibleGeoFormat = 'KML' | 'KMZ' | 'GeoJSON' | 'WKT' | 'WKB' | 'CSV';

export const SupportedFileExtensions = [
  'shp',
  'dbf',
  'prj',
  'shx',
  'cpg',
  'zip',
  'kml',
  'kmz',
  'geojson',
  'json',
  'wkt',
  'wkb',
  'csv',
] as const;

/**
 * Service responsible for parsing shapefile data (both zipped and non-zipped formats)
 *
 * Architecture:
 * - Encapsulates shapefile parsing domain logic
 * - Implemented as a singleton for easy dependency injection
 *
 * Usage:
 * ```typescript
 * const service = ShapefileParserService.getInstance();
 * const result = await service.parseShapefiles(shpfiles);
 * ```
 *
 * Testing:
 * The singleton pattern makes this service easy to test and mock:
 * ```typescript
 * // In tests, you can mock the instance methods
 * vi.spyOn(ShapefileParserService.getInstance(), 'parseShapefiles')
 *   .mockResolvedValue(mockData);
 *
 * // Or reset the singleton between tests if needed
 * vi.clearAllMocks();
 * ```
 */
export class ShapefileParserService {
  private static instance: ShapefileParserService;

  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {}

  /**
   * Get the singleton instance
   */
  static getInstance(): ShapefileParserService {
    if (!ShapefileParserService.instance) {
      ShapefileParserService.instance = new ShapefileParserService();
    }

    return ShapefileParserService.instance;
  }

  /**
   * Parse all shapefile data (both zipped and non-zipped)
   * @param shpfiles Array of shapefile data to parse
   * @returns Array of parsed shapefiles with their metadata (filtered to exclude undefined values)
   */
  async parseShapefiles(shpfiles: RogoShapefile[]): Promise<ParsedShapefile[]> {
    // Separate files into zipped, non-zipped, and convertible groups
    const { zippedFiles, nonZippedFiles, convertibleFiles } = this.categorizeFiles(shpfiles);

    console.log(
      `Found ${zippedFiles.length} zipped file(s), ${nonZippedFiles.length} non-zipped component(s), and ${convertibleFiles.length} convertible file(s)`,
    );

    const parsedFiles: Array<ParsedShapefile | undefined> = [];

    // Process convertible files (KML, KMZ, GeoJSON, WKT, WKB)
    if (convertibleFiles.length > 0) {
      console.log('Processing convertible geospatial files');

      const parsedConvertibleFiles = await Promise.all(
        convertibleFiles.map(async (file) => {
          try {
            return await this.convertAndParseGeoFile(file);
          } catch (err) {
            alertWarn(`Error converting file ${file.name}: ${err}`);

            return undefined;
          }
        }),
      );

      parsedFiles.push(...parsedConvertibleFiles);
      console.log(
        'Parsed convertible files: ',
        parsedConvertibleFiles.filter((f) => !!f),
      );
    }

    // Process non-zipped shapefile components
    if (nonZippedFiles.length > 0) {
      console.log('Processing non-zipped shapefile components');

      const shapeData = nonZippedFiles.map((shpfile) => {
        return { filename: shpfile.name, data: shpfile.data };
      });

      const parsedDataArray = await this.parseNonZippedShapefiles(shapeData);

      if (parsedDataArray.length > 0) {
        // Create a processed shapefile entry for each parsed shapefile
        for (const parsedData of parsedDataArray) {
          // Find the original .shp file that matches this basename
          const shpFile = nonZippedFiles.find((f) => {
            const filename = f.name.split('/').pop() || f.name;
            const basename = filename.replace(/\.(shp|dbf|prj|shx|cpg)$/i, '');

            return basename === parsedData.fileName && f.name.toLowerCase().endsWith('.shp');
          });

          parsedFiles.push({
            mode: shpFile?.mode || 'pts',
            source: shpFile?.source || 'unknown',
            name: parsedData.fileName || 'shapefile',
            data: parsedData,
          });
        }

        console.log(
          `Parsed ${parsedDataArray.length} non-zipped shapefile(s): ${parsedDataArray
            .map((d) => d.fileName)
            .join(', ')}`,
        );
      } else {
        console.warn('Failed to parse non-zipped shapefile components');
      }
    }

    // Process zipped shapefiles
    if (zippedFiles.length > 0) {
      console.log('Processing zipped shapefiles');

      const parsedZippedFiles = await Promise.all(
        zippedFiles.map(async (shpfile) => {
          try {
            const parsed = await GeoData.toGeoJSON(Buffer.from(shpfile.data), 'SHP');

            return { ...shpfile, data: parsed as shp.FeatureCollectionWithFilename };
          } catch (err) {
            alertWarn(`Error parsing shapefile ${shpfile.name}: ${err}`);

            return undefined;
          }
        }),
      );

      parsedFiles.push(...parsedZippedFiles);
      console.log('Parsed zipped shapefiles: ', parsedZippedFiles);
    }

    console.log('_shpfiles after parsing: ', parsedFiles);

    // Filter out any undefined or null files before returning
    const cleanedFiles = parsedFiles.filter((file) => !!file);

    return cleanedFiles;
  }

  /**
   * Convert a geospatial file to shapefile format and parse it
   * @param file The geospatial file to convert (KML, KMZ, GeoJSON, WKT, WKB, CSV)
   * @returns Parsed shapefile with metadata
   */
  private async convertAndParseGeoFile(file: RogoShapefile): Promise<ParsedShapefile | undefined> {
    const format = this.detectGeoFormat(file.name);

    if (!format) {
      console.error(`Could not detect format for file: ${file.name}`);

      return undefined;
    }

    console.log(`Converting ${format} file: ${file.name}`);

    try {
      // Extract the base filename without extension
      const baseFilename =
        file.name
          .split('/')
          .pop()
          ?.replace(/\.(kml|kmz|geojson|json|wkt|wkb|csv)$/i, '') || 'converted';

      // Handle CSV files separately
      if (format === 'CSV') {
        const csvParserService = CsvParserService.getInstance();
        const csvFeatureCollection = await csvParserService.parseCSV(file);

        return {
          mode: file.mode,
          source: file.source,
          name: baseFilename,
          data: csvFeatureCollection,
        };
      }

      // Convert the file data to the appropriate format for GeoData.toGeoJSON
      const dataToConvert = this.convertGeoFileData(file.data, format, file.name);

      // Convert directly to GeoJSON using shared-ts
      const parsed = await GeoData.toGeoJSON(dataToConvert, format);

      return {
        mode: file.mode,
        source: file.source,
        name: baseFilename,
        data: parsed as shp.FeatureCollectionWithFilename,
      };
    } catch (err) {
      console.error(`Error converting and parsing ${format} file ${file.name}:`, err);

      // Provide more detailed error information
      if (err instanceof Error) {
        console.error(`Error details: ${err.message}`);
        console.error(`Error stack: ${err.stack}`);

        // Check for specific XML parsing errors
        if (err.message.includes('getElementsByTagName')) {
          console.error(
            `XML parsing error detected. This may indicate that the ${format} file is malformed or the parser received invalid data.`,
          );
          console.error(`File name: ${file.name}`);
          console.error(`Buffer size: ${Buffer.from(file.data).length} bytes`);
        }
      }

      throw err;
    }
  }

  /**
   * Convert geospatial file data to the appropriate format for the parser
   *
   * Text-based formats (KML, GeoJSON, WKT) are converted to UTF-8 strings for XML/JSON parsers.
   * Binary formats (KMZ, WKB) are converted to Buffers.
   *
   * @param data The raw file data (typically an ArrayBuffer or ArrayBuffer-like object)
   * @param format The detected geospatial format
   * @param filename The filename (used for error messages)
   * @returns String for text formats, Buffer for binary formats
   */
  private convertGeoFileData(data: ArrayBuffer, format: ConvertibleGeoFormat, filename: string): Buffer | string {
    // Check if it's an ArrayBuffer-like object (has byteLength property)
    // This handles both true ArrayBuffers and ArrayBuffer-like objects from various environments
    const isArrayBufferLike =
      data instanceof ArrayBuffer || (typeof data === 'object' && data !== null && 'byteLength' in data);

    if (!isArrayBufferLike) {
      // Fallback for other types (shouldn't normally hit this)
      return Buffer.from(data);
    }

    // Convert to Uint8Array regardless of the exact buffer type
    const uint8Array = data instanceof Uint8Array ? data : new Uint8Array(data as ArrayBuffer);

    // Text-based formats need to be converted to strings for XML/JSON parsers
    const isTextFormat = format === 'KML' || format === 'GeoJSON' || format === 'WKT';

    if (isTextFormat) {
      const decoder = new TextDecoder('utf-8');
      const textContent = decoder.decode(uint8Array);

      // Validate that KML files contain valid XML
      if (format === 'KML' && !textContent.includes('<?xml') && !textContent.includes('<kml')) {
        throw new Error(`KML file ${filename} does not appear to contain valid KML XML content`);
      }

      return textContent;
    } else {
      // Binary formats (KMZ, WKB) are converted to Buffer
      return Buffer.from(uint8Array);
    }
  }

  /**
   * Parse non-zipped shapefile components by zipping them and using GeoData.toGeoJSON
   * @private Internal method - use parseShapefiles() instead
   * @param components Array of shapefile components with filename and data
   * @returns Array of FeatureCollections (one per complete shapefile found)
   */
  private async parseNonZippedShapefiles(
    components: { filename: string; data: ArrayBuffer }[],
  ): Promise<shp.FeatureCollectionWithFilename[]> {
    // Group files by basename (everything before the extension)
    const fileGroups = new Map<string, Map<string, ArrayBuffer>>();

    for (const component of components) {
      const filename = component.filename.split('/').pop() || component.filename;
      const basename = filename.replace(/\.(shp|dbf|prj|shx|cpg)$/i, '');
      const extension = filename.match(/\.(shp|dbf|prj|shx|cpg)$/i)?.[1]?.toLowerCase();

      if (!extension) {
        continue;
      }

      if (!fileGroups.has(basename)) {
        fileGroups.set(basename, new Map());
      }

      fileGroups.get(basename)!.set(extension, component.data);
    }

    // Parse all complete shapefiles (must have .shp)
    const parsedShapefiles: shp.FeatureCollectionWithFilename[] = [];

    for (const [basename, group] of fileGroups.entries()) {
      if (!group.has('shp')) {
        continue;
      }

      try {
        // Zip components into a single buffer for GeoData.toGeoJSON
        const zip = new JSZip();

        for (const [ext, data] of group.entries()) {
          zip.file(`${basename}.${ext}`, data);
        }

        const zipBuffer = await zip.generateAsync({ type: 'arraybuffer' });
        const parsed = await GeoData.toGeoJSON(Buffer.from(zipBuffer), 'SHP');
        const featureCollection = { ...parsed, fileName: basename } as shp.FeatureCollectionWithFilename;

        parsedShapefiles.push(featureCollection);
      } catch (err) {
        console.error(`Error parsing shapefile components for ${basename}:`, err);
        // Continue to next group
      }
    }

    console.log(
      `Successfully parsed ${parsedShapefiles.length} non-zipped shapefile(s): ${parsedShapefiles
        .map((s) => s.fileName)
        .join(', ')}`,
    );

    return parsedShapefiles;
  }

  /**
   * Categorize files into zipped, non-zipped, and convertible formats
   * @param shpfiles Array of shapefile data
   * @returns Object containing zipped, non-zipped, and convertible file arrays
   */
  private categorizeFiles(shpfiles: RogoShapefile[]): {
    zippedFiles: RogoShapefile[];
    nonZippedFiles: RogoShapefile[];
    convertibleFiles: RogoShapefile[];
  } {
    const zippedFiles: RogoShapefile[] = [];
    const nonZippedFiles: RogoShapefile[] = [];
    const convertibleFiles: RogoShapefile[] = [];

    shpfiles.forEach((file) => {
      const filename = file.name.toLowerCase();

      if (
        filename.endsWith('.shp') ||
        filename.endsWith('.dbf') ||
        filename.endsWith('.prj') ||
        filename.endsWith('.shx') ||
        filename.endsWith('.cpg')
      ) {
        nonZippedFiles.push(file);
      } else if (filename.endsWith('.zip')) {
        zippedFiles.push(file);
      } else if (this.isConvertibleFormat(filename)) {
        convertibleFiles.push(file);
      } else {
        console.warn(`Unknown file type: ${file.name}`);
      }
    });

    return { zippedFiles, nonZippedFiles, convertibleFiles };
  }

  /**
   * Check if a file is in a convertible format (KML, KMZ, GeoJSON, WKT, WKB, CSV)
   * @param filename The filename to check
   * @returns true if the file is in a convertible format
   */
  private isConvertibleFormat(filename: string): boolean {
    const lowerFilename = filename.toLowerCase();

    return (
      lowerFilename.endsWith('.kml') ||
      lowerFilename.endsWith('.kmz') ||
      lowerFilename.endsWith('.geojson') ||
      lowerFilename.endsWith('.json') ||
      lowerFilename.endsWith('.wkt') ||
      lowerFilename.endsWith('.wkb') ||
      lowerFilename.endsWith('.csv')
    );
  }

  /**
   * Detect the geospatial format from a filename
   * @param filename The filename to analyze
   * @returns The detected format or null if unknown
   */
  private detectGeoFormat(filename: string): ConvertibleGeoFormat | null {
    const lowerFilename = filename.toLowerCase();

    if (lowerFilename.endsWith('.kml')) {
      return 'KML';
    }

    if (lowerFilename.endsWith('.kmz')) {
      return 'KMZ';
    }

    if (lowerFilename.endsWith('.geojson') || lowerFilename.endsWith('.json')) {
      return 'GeoJSON';
    }

    if (lowerFilename.endsWith('.wkt')) {
      return 'WKT';
    }

    if (lowerFilename.endsWith('.wkb')) {
      return 'WKB';
    }

    if (lowerFilename.endsWith('.csv')) {
      return 'CSV';
    }

    return null;
  }
}

/**
 * Type definition for a parsed shapefile with its metadata
 */
export type ParsedShapefile = {
  mode: RogoShapefileType;
  source: string;
  name: string;
  data: shp.FeatureCollectionWithFilename | shp.FeatureCollectionWithFilename[];
};
